/**
 * Generate a slug from a pet name and occurrence number
 * @param {string} name - Pet name
 * @param {number} occurrence - The occurrence number (1, 2, 3, etc.)
 * @returns {string} - Slug like 'fluffy-1'
 */
export const generatePetSlug = (name, occurrence = 1) => {
  const slugName = name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  return `${slugName}-${occurrence}`;
};

/**
 * Parse a pet slug and return name and occurrence
 * @param {string} slug - Slug like 'fluffy-1'
 * @returns {object} - { nameSlug: 'fluffy', occurrence: 1 }
 */
export const parsePetSlug = (slug) => {
  const parts = slug.split('-');
  const occurrence = parseInt(parts[parts.length - 1]);
  
  if (isNaN(occurrence)) {
    return { nameSlug: slug, occurrence: 1 };
  }
  
  const nameSlug = parts.slice(0, -1).join('-');
  return { nameSlug, occurrence };
};

/**
 * Find a pet by slug from a list of pets
 * Matches pets by name and finds the nth occurrence
 * @param {Array} pets - Array of pet objects
 * @param {string} slug - Slug like 'fluffy-1'
 * @returns {object|null} - The pet object or null if not found
 */
export const findPetBySlug = (pets, slug) => {
  if (!pets || !Array.isArray(pets)) return null;

  const { nameSlug, occurrence } = parsePetSlug(slug);
  
  // Find all pets with matching name (case-insensitive)
  const matchingPets = pets.filter(pet => 
    pet.name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '') === nameSlug
  );

  // Return the nth occurrence (1-indexed)
  if (matchingPets.length >= occurrence) {
    return matchingPets[occurrence - 1];
  }

  return null;
};

/**
 * Get the occurrence number of a pet within a list of pets with the same name
 * @param {object} pet - The pet object with a 'name' property
 * @param {Array} pets - Array of all pet objects
 * @returns {number} - The occurrence number (1, 2, 3, etc.)
 */
export const getPetOccurrence = (pet, pets) => {
  if (!pet || !pets || !Array.isArray(pets)) return 1;

  const sameName = pets.filter(p => 
    p.name.toLowerCase().trim() === pet.name.toLowerCase().trim()
  );

  const index = sameName.findIndex(p => p._id === pet._id);
  return index >= 0 ? index + 1 : 1;
};

/**
 * Generate a slug from a happy ending title with occurrence number
 * @param {string} title - Happy ending title
 * @param {number} occurrence - The occurrence number (1, 2, 3, etc.)
 * @returns {string} - Slug like 'luna-finds-her-forever-home-1'
 */
export const generateHappyEndingSlug = (title, occurrence = 1) => {
  if (!title) return '';
  
  // Transliterate special characters (Bosnian/Serbian/Croatian)
  const transliterationMap = {
    'š': 's',
    'Š': 's',
    'ž': 'z',
    'Ž': 'z',
    'đ': 'd',
    'Đ': 'd',
    'č': 'c',
    'Č': 'c',
    'ć': 'c',
    'Ć': 'c',
  };
  
  let slugified = title.toLowerCase().trim();
  
  // Apply transliteration
  Object.entries(transliterationMap).forEach(([char, replacement]) => {
    slugified = slugified.replace(new RegExp(char, 'g'), replacement);
  });
  
  // Convert to slug
  const baseSlug = slugified
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/[^a-z0-9-]/g, '') // Remove special characters (keep only a-z, 0-9, -)
    .replace(/-+/g, '-') // Replace multiple dashes with single dash
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing dashes
  
  return `${baseSlug}-${occurrence}`;
};

/**
 * Parse a happy ending slug and return title slug and occurrence
 * @param {string} slug - Slug like 'luna-finds-her-forever-home-1'
 * @returns {object} - { titleSlug: 'luna-finds-her-forever-home', occurrence: 1 }
 */
export const parseHappyEndingSlug = (slug) => {
  const parts = slug.split('-');
  const occurrence = parseInt(parts[parts.length - 1]);
  
  if (isNaN(occurrence)) {
    return { titleSlug: slug, occurrence: 1 };
  }
  
  const titleSlug = parts.slice(0, -1).join('-');
  return { titleSlug, occurrence };
};

/**
 * Get the occurrence number of a happy ending within a list with the same title
 * @param {object} happyEnding - The happy ending object with title fields
 * @param {Array} happyEndings - Array of all happy ending objects
 * @param {string} lang - Language code ('bs' or 'en')
 * @returns {number} - The occurrence number (1, 2, 3, etc.)
 */
export const getHappyEndingOccurrence = (happyEnding, happyEndings, lang = 'bs') => {
  if (!happyEnding || !happyEndings || !Array.isArray(happyEndings)) return 1;

  const titleToMatch = happyEnding[`title_${lang}`] || happyEnding.title_en || happyEnding.title_bs;
  
  const sameTitle = happyEndings.filter(he => {
    const heTitle = he[`title_${lang}`] || he.title_en || he.title_bs;
    return heTitle && heTitle.toLowerCase().trim() === titleToMatch.toLowerCase().trim();
  });

  const index = sameTitle.findIndex(he => he._id === happyEnding._id);
  return index >= 0 ? index + 1 : 1;
};

/**
 * Find a happy ending by slug from a list
 * @param {string} slug - Slug like 'luna-finds-her-forever-home-1'
 * @param {Array} happyEndings - Array of happy ending objects
 * @param {string} lang - Language code ('bs' or 'en')
 * @returns {object|null} - The happy ending object or null if not found
 */
export const findHappyEndingBySlug = (slug, happyEndings, lang = 'bs') => {
  if (!happyEndings || !Array.isArray(happyEndings) || !slug) return null;
  
  const { titleSlug, occurrence } = parseHappyEndingSlug(slug);
  
  // Generate slugs for both languages and find matches
  const matchingEndings = happyEndings.filter((ending) => {
    const slugEn = generateHappyEndingSlug(ending.title_en || '', 1).split('-').slice(0, -1).join('-');
    const slugBs = generateHappyEndingSlug(ending.title_bs || '', 1).split('-').slice(0, -1).join('-');
    return slugEn === titleSlug || slugBs === titleSlug;
  });

  // Return the nth occurrence (1-indexed)
  if (matchingEndings.length >= occurrence) {
    return matchingEndings[occurrence - 1];
  }

  return null;
};

/**
 * Generate a slug from a news title with occurrence number
 * @param {string} title - News title
 * @param {number} occurrence - The occurrence number (1, 2, 3, etc.)
 * @returns {string} - Slug like 'new-dogs-arrived-1'
 */
export const generateNewsSlug = (title, occurrence = 1) => {
  if (!title) return '';
  
  // Transliterate special characters (Bosnian/Serbian/Croatian)
  const transliterationMap = {
    'š': 's',
    'Š': 's',
    'ž': 'z',
    'Ž': 'z',
    'đ': 'd',
    'Đ': 'd',
    'č': 'c',
    'Č': 'c',
    'ć': 'c',
    'Ć': 'c',
  };
  
  let slugified = title.toLowerCase().trim();
  
  // Apply transliteration
  Object.entries(transliterationMap).forEach(([char, replacement]) => {
    slugified = slugified.replace(new RegExp(char, 'g'), replacement);
  });
  
  // Convert to slug
  const baseSlug = slugified
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/[^a-z0-9-]/g, '') // Remove special characters (keep only a-z, 0-9, -)
    .replace(/-+/g, '-') // Replace multiple dashes with single dash
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing dashes
  
  return `${baseSlug}-${occurrence}`;
};

/**
 * Get the occurrence number of a news post within a list with the same title
 * @param {object} newsPost - The news post object with title fields
 * @param {Array} newsPosts - Array of all news post objects
 * @param {string} lang - Language code ('bs' or 'en')
 * @returns {number} - The occurrence number (1, 2, 3, etc.)
 */
export const getNewsOccurrence = (newsPost, newsPosts, lang = 'bs') => {
  if (!newsPost || !newsPosts || !Array.isArray(newsPosts)) return 1;

  const titleToMatch = newsPost[`title_${lang}`] || newsPost.title_en || newsPost.title_bs;
  
  const sameTitle = newsPosts.filter(post => {
    const postTitle = post[`title_${lang}`] || post.title_en || post.title_bs;
    return postTitle && postTitle.toLowerCase().trim() === titleToMatch.toLowerCase().trim();
  });

  const index = sameTitle.findIndex(post => post._id === newsPost._id);
  return index >= 0 ? index + 1 : 1;
};

/**
 * Find a news post by slug from a list
 * @param {string} slug - Slug like 'new-dogs-arrived-1'
 * @param {Array} newsPosts - Array of news post objects
 * @param {string} lang - Language code ('bs' or 'en')
 * @returns {object|null} - The news post object or null if not found
 */
export const findNewsBySlug = (slug, newsPosts, lang = 'bs') => {
  if (!newsPosts || !Array.isArray(newsPosts) || !slug) return null;
  
  // Parse slug to get the base slug and occurrence
  const parts = slug.split('-');
  const occurrence = parseInt(parts[parts.length - 1]);
  
  const titleSlug = isNaN(occurrence) ? slug : parts.slice(0, -1).join('-');
  const occurrenceNum = isNaN(occurrence) ? 1 : occurrence;
  
  // Generate slugs for both languages and find matches
  const matchingPosts = newsPosts.filter((post) => {
    const slugEn = generateNewsSlug(post.title_en || '', 1).split('-').slice(0, -1).join('-');
    const slugBs = generateNewsSlug(post.title_bs || '', 1).split('-').slice(0, -1).join('-');
    return slugEn === titleSlug || slugBs === titleSlug;
  });

  // Return the nth occurrence (1-indexed)
  if (matchingPosts.length >= occurrenceNum) {
    return matchingPosts[occurrenceNum - 1];
  }

  return null;
};
